// src/pages/Admin/Dashboard/PageVisitsAnalytics.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FiArrowUpRight,
  FiBarChart2,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiHome,
  FiRefreshCw,
  FiSearch,
  FiTrendingUp,
} from "react-icons/fi";

import { countVisitesByPage } from "../../../services/visitesServices";

const FRONT_URL = process.env.REACT_APP_URL || window.location.origin;

const PERIODS = [
  { value: "today", label: "Aujourd’hui" },
  { value: "7", label: "7 jours" },
  { value: "30", label: "30 jours" },
  { value: "90", label: "90 jours" },
  { value: "365", label: "1 an" },
  { value: "all", label: "Tout" },
];

const LIMITS = [5, 10, 20, 50];

const normalizeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toYMD = (date) => {
  const dt = new Date(date);
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDateRangeFromPeriod = (period) => {
  const end = new Date();

  if (period === "all") {
    return {};
  }

  if (period === "today") {
    const today = toYMD(end);

    return {
      from: today,
      to: today,
    };
  }

  const days = Number(period) || 30;
  const start = new Date();

  start.setDate(end.getDate() - (days - 1));

  return {
    from: toYMD(start),
    to: toYMD(end),
  };
};

const formatPagePath = (pagePath) => {
  if (!pagePath || pagePath === "home" || pagePath === "/") {
    return "/";
  }

  return pagePath;
};

const getPageLabel = (item) => {
  if (item.page_label && item.page_label !== item.page_path) {
    return item.page_label;
  }

  if (item.page_path === "home") {
    return "Accueil";
  }

  return item.page_path || "Page inconnue";
};

const getPublicPath = (pagePath) => {
  if (!pagePath || pagePath === "home" || pagePath === "/") {
    return "/";
  }

  return pagePath.startsWith("/") ? pagePath : `/${pagePath}`;
};

const getAbsolutePublicUrl = (pagePath) => {
  try {
    return new URL(getPublicPath(pagePath), FRONT_URL).href;
  } catch (_) {
    return getPublicPath(pagePath);
  }
};

const getPerformance = ({ index, count, share }) => {
  if (count <= 0) {
    return {
      label: "Aucune visite",
      className:
        "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    };
  }

  if (index === 0 && count >= 5) {
    return {
      label: "Page leader",
      className:
        "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
    };
  }

  if (share >= 20) {
    return {
      label: "Très consultée",
      className:
        "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900",
    };
  }

  if (share >= 8) {
    return {
      label: "Consultée",
      className:
        "bg-indigo-50 text-indigo-700 border-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900",
    };
  }

  if (count >= 3) {
    return {
      label: "À surveiller",
      className:
        "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
    };
  }

  return {
    label: "Peu consultée",
    className:
      "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900",
  };
};

const StatCard = ({ icon: Icon, label, value, hint, highlight = false }) => {
  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm ${
        highlight
          ? "border-sky-200 bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30"
          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
            {value}
          </p>

          {hint ? (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {hint}
            </p>
          ) : null}
        </div>

        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full ${
            highlight
              ? "bg-white text-sky-600 dark:bg-slate-900 dark:text-sky-300"
              : "bg-sky-50 text-sky-600 dark:bg-sky-950/50 dark:text-sky-300"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

export default function PageVisitsAnalytics() {
  const [rawData, setRawData] = useState([]);
  const [period, setPeriod] = useState("today");
  const [search, setSearch] = useState("");
  const [excludeHome, setExcludeHome] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(10);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchVisitsByPage = async () => {
    try {
      setLoading(true);

      const params = {
        ...getDateRangeFromPeriod(period),
        limit: 300,
        include_internal: "0",
      };

      const response = await countVisitesByPage(params);

      setRawData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur analyse visites par page :", error);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitsByPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const normalizedData = useMemo(() => {
    const rows = Array.isArray(rawData) ? rawData : [];

    return rows
      .map((item) => {
        const pagePath = item.page_path || "home";
        const count = normalizeNumber(item.total_visits ?? item.compteur);

        return {
          page_path: pagePath,
          page_label: getPageLabel(item),
          page_display: getPageLabel(item),
          public_path: formatPagePath(pagePath),
          total_visits: count,
        };
      })
      .filter((item) => {
        if (excludeHome && item.page_path === "home") {
          return false;
        }

        return true;
      })
      .sort((a, b) => b.total_visits - a.total_visits);
  }, [rawData, excludeHome]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return normalizedData;

    return normalizedData.filter((item) => {
      return (
        item.page_label.toLowerCase().includes(q) ||
        item.public_path.toLowerCase().includes(q)
      );
    });
  }, [normalizedData, search]);

  const stats = useMemo(() => {
    const total = normalizedData.reduce(
      (sum, item) => sum + item.total_visits,
      0,
    );

    const pagesCount = normalizedData.length;
    const topPage = normalizedData[0] || null;

    const homeVisits =
      normalizedData.find((item) => item.page_path === "home")?.total_visits ||
      0;

    const nonHomeTotal = normalizedData
      .filter((item) => item.page_path !== "home")
      .reduce((sum, item) => sum + item.total_visits, 0);

    return {
      total,
      pagesCount,
      topPage,
      homeVisits,
      nonHomeTotal,
    };
  }, [normalizedData]);

  const enrichedData = useMemo(() => {
    return filteredData.map((item, index) => {
      const share = stats.total
        ? Math.round((item.total_visits / stats.total) * 1000) / 10
        : 0;

      return {
        ...item,
        rank: index + 1,
        share,
        performance: getPerformance({
          index,
          count: item.total_visits,
          share,
        }),
      };
    });
  }, [filteredData, stats.total]);

  const displayedData = useMemo(() => {
    return enrichedData.slice(0, visibleLimit);
  }, [enrichedData, visibleLimit]);

  const chartData = useMemo(() => {
    return displayedData.slice(0, 8).map((item) => ({
      ...item,
      page_display:
        item.page_display.length > 24
          ? `${item.page_display.slice(0, 24)}…`
          : item.page_display,
    }));
  }, [displayedData]);

  const periodLabel =
    PERIODS.find((item) => item.value === period)?.label || "Aujourd’hui";

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-300">
            Analyse du trafic public
          </p>

          <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Visites des pages du site
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Les pages d’administration sont ignorées. L’affichage est centré sur
            les visites du jour et les pages publiques les plus consultées.
          </p>
        </div>

        <button
          type="button"
          onClick={fetchVisitsByPage}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={FiEye}
          label="Visites"
          value={loading ? "…" : stats.total}
          hint={`Période : ${periodLabel}`}
          highlight={period === "today"}
        />

        <StatCard
          icon={FiHome}
          label="Accueil"
          value={loading ? "…" : stats.homeVisits}
          hint={
            excludeHome ? "Accueil masqué dans l’analyse" : "Page d’accueil"
          }
        />

        <StatCard
          icon={FiTrendingUp}
          label="Page principale"
          value={loading ? "…" : stats.topPage?.page_label || "-"}
          hint={
            stats.topPage
              ? `${stats.topPage.total_visits} visite${
                  stats.topPage.total_visits > 1 ? "s" : ""
                }`
              : "Aucune donnée"
          }
        />

        <StatCard
          icon={FiBarChart2}
          label="Pages suivies"
          value={loading ? "…" : stats.pagesCount}
          hint={`${displayedData.length} affichée${
            displayedData.length > 1 ? "s" : ""
          } sur ${filteredData.length}`}
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 p-4 dark:border-slate-800 md:p-5">
          <div className="grid gap-3 xl:grid-cols-[1fr_auto_auto_auto] xl:items-center">
            <div className="relative">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une page publique..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-950"
              />
            </div>

            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-950"
            >
              {PERIODS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select
              value={visibleLimit}
              onChange={(e) => setVisibleLimit(Number(e.target.value))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-950"
            >
              {LIMITS.map((limit) => (
                <option key={limit} value={limit}>
                  Top {limit}
                </option>
              ))}
            </select>

            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
              <span>Masquer accueil</span>
              <input
                type="checkbox"
                checked={excludeHome}
                onChange={(e) => setExcludeHome(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
            </label>
          </div>
        </div>

        <div className="grid gap-0 xl:grid-cols-[1.05fr_1fr]">
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 md:p-5 xl:border-b-0 xl:border-r">
            <div className="mb-4">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                Top des pages publiques
              </h3>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Affichage limité pour garder une lecture claire.
              </p>
            </div>

            {chartData.length === 0 ? (
              <div className="flex h-[320px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                {loading
                  ? "Chargement des visites..."
                  : "Aucune donnée à afficher."}
              </div>
            ) : (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 8, right: 18, bottom: 8, left: 18 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

                    <XAxis
                      type="number"
                      stroke="#64748b"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />

                    <YAxis
                      type="category"
                      dataKey="page_display"
                      width={125}
                      stroke="#64748b"
                      tick={{ fontSize: 11 }}
                    />

                    <Tooltip
                      formatter={(value) => [value, "Visites"]}
                      labelFormatter={(_, payload) => {
                        const row = payload?.[0]?.payload;
                        return row?.page_label || "";
                      }}
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        border: "none",
                        borderRadius: 12,
                        color: "#f9fafb",
                        fontSize: 12,
                      }}
                      cursor={{ fill: "rgba(148, 163, 184, 0.12)" }}
                    />

                    <Bar
                      dataKey="total_visits"
                      name="Visites"
                      fill="#0ea5e9"
                      radius={[0, 10, 10, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="p-4 md:p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  Lecture rapide
                </h3>

                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Les pages à suivre en priorité sur la période sélectionnée.
                </p>
              </div>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {periodLabel}
              </span>
            </div>

            <div className="space-y-3">
              {displayedData.map((item) => (
                <div
                  key={item.page_path}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                        #{item.rank} {item.page_label}
                      </p>

                      <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                        {item.public_path}
                      </p>
                    </div>

                    <span
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${item.performance.className}`}
                    >
                      {item.performance.label}
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      <strong className="text-slate-900 dark:text-white">
                        {item.total_visits}
                      </strong>{" "}
                      visite{item.total_visits > 1 ? "s" : ""}
                    </span>

                    <span>{item.share}% du trafic public</span>
                  </div>

                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white dark:bg-slate-900">
                    <div
                      className="h-full rounded-full bg-sky-500"
                      style={{ width: `${Math.min(item.share, 100)}%` }}
                    />
                  </div>
                </div>
              ))}

              {!loading && displayedData.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                  Aucune page ne correspond à votre recherche.
                </div>
              ) : null}
            </div>

            {enrichedData.length > visibleLimit ? (
              <button
                type="button"
                onClick={() => setVisibleLimit((current) => current + 10)}
                className="mt-4 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              >
                Afficher 10 pages de plus
              </button>
            ) : null}
          </div>
        </div>

        <div className="border-t border-slate-200 p-4 dark:border-slate-800 md:p-5">
          <button
            type="button"
            onClick={() => setShowTable((current) => !current)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            {showTable ? <FiChevronUp /> : <FiChevronDown />}
            {showTable ? "Masquer le détail" : "Voir le détail des pages"}
          </button>
        </div>

        {showTable ? (
          <div className="border-t border-slate-200 dark:border-slate-800">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Rang
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Page
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                      Visites
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                      Part
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Analyse
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                      Voir
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
                  {displayedData.map((item) => (
                    <tr
                      key={item.page_path}
                      className="transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                        #{item.rank}
                      </td>

                      <td className="px-4 py-3">
                        <div className="max-w-[320px]">
                          <p className="truncate font-semibold text-slate-900 dark:text-white">
                            {item.page_label}
                          </p>

                          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {item.public_path}
                          </p>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                        {item.total_visits}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600 dark:text-slate-300">
                        {item.share}%
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${item.performance.className}`}
                        >
                          {item.performance.label}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            window.open(
                              getAbsolutePublicUrl(item.page_path),
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                        >
                          Ouvrir
                          <FiArrowUpRight className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!loading && displayedData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        Aucune donnée disponible.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
